"use client";

// T-04: The phantom CRUD endpoints (training-centre-add, training-centre-update, etc.)
// do NOT exist in the backend. The "training-centre/training-centre-list" endpoint is
// user-facing only (takes { user_id, role }), not an admin CRUD resource.
// This page is a read-only view with a Play action that routes into the interactive player.

import { useSession } from "next-auth/react";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { BookOpen, Loader2, Play } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface TrainingCourse {
  _id: string;
  title?: string;
  lesson_name?: string;
  category_name?: string;
  status?: number;
}

export default function TrainingCenterPage() {
  const { data: session } = useSession();
  const router = useRouter();
  const baseUrl = process.env.NEXT_PUBLIC_API_CLOUDFRONT ?? "https://api.divineinfinitebeing.com/";

  const { data, isLoading } = useQuery({
    queryKey: ["training-centre-list"],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}training-centre/training-centre-list`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token ?? ""}`,
        },
        body: JSON.stringify({}),
      });
      const json = await res.json();
      return (json?.results?.res ?? json?.results ?? []) as TrainingCourse[];
    },
    enabled: !!session?.token,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-7 w-1 rounded-full shrink-0" style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }} />
          <div>
            <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <BookOpen className="size-4" />
              Training Center
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Training course overview — manage courses via Training Categories and Lessons
            </p>
          </div>
        </div>
        <Button
          onClick={() => router.push("/admin/training/center/play")}
          style={{ background: "linear-gradient(135deg, #ffa800, #ff7e00)", border: "none" }}
          className="text-white font-semibold"
        >
          <Play className="size-3.5 mr-2 fill-white" />
          Start Training
        </Button>
      </div>

      <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
        <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
          <CardTitle className="flex items-center gap-2 text-sm">
            <span className="h-3.5 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
            Available Training Content
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading training content...
            </div>
          ) : !data?.length ? (
            <div className="py-12 text-center text-muted-foreground text-sm">
              <BookOpen className="mx-auto size-8 opacity-30 mb-2" />
              No training content available. Add categories and lessons to get started.
            </div>
          ) : (
            <div className="space-y-2">
              {data.map((course) => (
                <div
                  key={course._id}
                  className="flex items-center justify-between px-4 py-3 rounded-lg"
                  style={{ background: "hsl(240 20% 18%)", border: "1px solid hsl(240 20% 26%)" }}
                >
                  <div>
                    <p className="text-sm font-medium">{course.title ?? course.category_name ?? course.lesson_name ?? "Untitled"}</p>
                    {course.category_name && course.title && (
                      <p className="text-xs text-muted-foreground mt-0.5">{course.category_name}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="border text-[10px]" style={
                      course.status === 1
                        ? { background: "rgba(22,160,133,0.15)", color: "#16a085", borderColor: "rgba(22,160,133,0.3)" }
                        : { background: "rgba(255,168,0,0.1)", color: "#ffa800", borderColor: "rgba(255,168,0,0.3)" }
                    }>
                      {course.status === 1 ? "Active" : "Inactive"}
                    </Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-[11px] gap-1"
                      style={{ color: "#ffa800" }}
                      onClick={() => router.push(`/admin/training/center/play/${course._id}`)}
                    >
                      <Play className="size-3 fill-current" />
                      Play
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
